import React from 'react'
import { Company, UpdateCompanyDto, LicenseType } from '@/types/company'
import { useToast } from '@/components/ui/use-toast'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'

interface EditCompanyModalProps {
  company: Company
  isOpen: boolean
  onClose: () => void
  onSubmit: (id: string, data: UpdateCompanyDto) => Promise<void>
}

interface FormData {
  name: string
  phone: string
  city: string
  sector: string
  aiMusicDownloadEnabled: boolean
  aiMusicPlaylistEnabled: boolean
  license: {
    type: LicenseType
    count: number
    isActive: boolean
    expiryDate: string
  }
}

export function EditCompanyModal({
  company,
  isOpen,
  onClose,
  onSubmit,
}: EditCompanyModalProps) {
  const { toast } = useToast()
  
  // useEffect ile formData'yı başlatalım ve firma değiştiğinde güncelleyelim
  const [formData, setFormData] = React.useState<FormData>({
    name: '',
    phone: '',
    city: '',
    sector: '',
    aiMusicDownloadEnabled: false,
    aiMusicPlaylistEnabled: true,
    license: {
      type: 'trial',
      count: 1,
      isActive: false,
      expiryDate: new Date().toISOString().split('T')[0],
    },
  })
  
  // Firma değiştiğinde formData'yı güncelle
  React.useEffect(() => {
    console.log('EditCompanyModal: Company prop değişti', JSON.stringify(company, null, 2))
    if (company) {
      setFormData({
        name: company.name || '',
        phone: company.phone || '',
        city: company.city || '',
        sector: company.sector || '',
        aiMusicDownloadEnabled: (company as any).aiMusicPackageDetails?.downloadEnabled === true,
        aiMusicPlaylistEnabled: (company as any).aiMusicPackageDetails?.playlistEnabled !== false,
        license: {
          type: company.license?.type || 'trial',
          count: company.license?.count || 1,
          isActive: company.license?.isActive || false,
          expiryDate: company.license?.expiryDate 
            ? new Date(company.license.expiryDate).toISOString().split('T')[0] 
            : new Date().toISOString().split('T')[0],
        },
      })
    }
  }, [company, company._id])

  React.useEffect(() => {
    console.log('Modal Form Data', formData)
  }, [formData])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const updateData: UpdateCompanyDto = {
        name: formData.name,
        phone: formData.phone,
        city: formData.city,
        sector: formData.sector,
        aiMusicDownloadEnabled: formData.aiMusicDownloadEnabled,
        aiMusicPlaylistEnabled: formData.aiMusicPlaylistEnabled,
        license: {
          type: formData.license.type,
          count: Number(formData.license.count),
          isActive: formData.license.isActive,
          expiryDate: formData.license.expiryDate,
        },
      } as any

      await onSubmit(company._id, updateData)
      toast({
        title: 'Başarılı',
        description: 'Firma başarıyla güncellendi.',
      })
      onClose()
    } catch (error) {
      console.error('Firma güncellenirken hata:', error)
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Firma güncellenirken bir hata oluştu.',
      })
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Firma Düzenle</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Firma Adı</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">Telefon</Label>
            <Input
              id="phone"
              value={formData.phone}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, phone: e.target.value }))
              }
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="city">Şehir</Label>
            <Input
              id="city"
              value={formData.city}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, city: e.target.value }))
              }
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sector">Sektör</Label>
            <Input
              id="sector"
              value={formData.sector}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, sector: e.target.value }))
              }
              required
            />
          </div>
          <div className="space-y-4">
            <h3 className="font-medium">Lisans Bilgileri</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="licenseType">Lisans Tipi</Label>
                <select
                  id="licenseType"
                  className="w-full px-3 py-2 border rounded-md"
                  value={formData.license.type}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      license: { ...prev.license, type: e.target.value as LicenseType },
                    }))
                  }
                >
                  <option value="trial">Deneme</option>
                  <option value="premium">Premium</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="licenseCount">Lisans Sayısı</Label>
                <Input
                  id="licenseCount"
                  type="number"
                  min="1"
                  value={formData.license.count}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      license: { ...prev.license, count: parseInt(e.target.value) },
                    }))
                  }
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="licenseExpiry">Bitiş Tarihi</Label>
              <Input
                id="licenseExpiry"
                type="date"
                value={formData.license.expiryDate}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    license: { ...prev.license, expiryDate: e.target.value },
                  }))
                }
              />
            </div>
            <div className="grid gap-4 py-4">
              <div className="flex items-center gap-4">
                <Label htmlFor="license-status">Lisans Durumu</Label>
                <Checkbox
                  id="license-status"
                  checked={formData.license.isActive}
                  onChange={(event) => {
                    setFormData({
                      ...formData,
                      license: {
                        ...formData.license,
                        isActive: event.target.checked
                      }
                    });
                  }}
                />
              </div>
            </div>
          </div>

          <div className="space-y-3 border-t pt-4">
            <h3 className="font-medium">AI Müzik Ayarları</h3>
            <div className="flex items-start gap-3">
              <Checkbox
                id="ai-music-download"
                checked={formData.aiMusicDownloadEnabled}
                onChange={(event: any) => {
                  setFormData((prev) => ({
                    ...prev,
                    aiMusicDownloadEnabled: event.target.checked
                  }));
                }}
              />
              <div className="flex-1">
                <Label htmlFor="ai-music-download" className="cursor-pointer">AI Müzik İndirmeye İzin Ver</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Aktif edildiğinde firma yöneticileri üretilen AI şarkıları indirebilir. Pasif ise "Bizimle iletişime geçin" modalı çıkar.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Checkbox
                id="ai-music-playlist"
                checked={formData.aiMusicPlaylistEnabled}
                onChange={(event: any) => {
                  setFormData((prev) => ({
                    ...prev,
                    aiMusicPlaylistEnabled: event.target.checked
                  }));
                }}
              />
              <div className="flex-1">
                <Label htmlFor="ai-music-playlist" className="cursor-pointer">Playlist'e Eklemeye İzin Ver</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Aktif edildiğinde player'daki "Çalma Listesine Ekle" butonu çalışır. Pasif ise buton gizlenir veya iletişim modalı çıkar.
                </p>
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onClose}>
              İptal
            </Button>
            <Button type="submit">Kaydet</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
